import { useQuery } from '@apollo/client';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef, GridColumnVisibilityModel, GridRowParams } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { Tooltip } from '~/components/Tooltip';
import { Count } from '~/components/count';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { SearchBar } from '~/components/forms/search-bar';
import { Link } from '~/components/link';
import { useLink } from '~/hooks/link';
import { useLocation } from '~/hooks/location';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { formatDate, formatTimeAgo } from '~/lib/datetime';
import {
  TemplateUsageDocument,
  type TemplateUsage__ContentItem as ContentItem,
} from '../queries/TemplateUsage.generated';
import { Layout, UsageMenu } from './components';

const ContentItemName = ({ contentItem }: { contentItem: ContentItem }) => {
  return (
    <Box display="flex" alignItems="center" gap={1}>
      {contentItem.thumbnailUri && (
        <Box
          component="img"
          src={contentItem.thumbnailUri}
          alt={contentItem.name}
          sx={{
            width: 24,
            height: 24,
            objectFit: 'cover',
            borderRadius: 0.5,
          }}
        />
      )}
      {contentItem.name}
    </Box>
  );
};

const ContentFolderLink = ({ contentItem }: { contentItem: ContentItem }) => {
  const link = useLink();

  return (
    <Link
      onClick={(e) => e.stopPropagation()}
      to={link(`/content/${contentItem.contentFolder.id}`, contentItem.contentFolder.network.id)}
    >
      {contentItem.contentFolder.name}
    </Link>
  );
};

const useColumns = (): GridColDef<ContentItem>[] => {
  const { t } = useTranslation();

  return [
    {
      field: 'id',
      width: 70,
      headerName: t('pages.templates.usage.id_column'),
      resizable: false,
    },
    {
      field: 'name',
      flex: 2,
      minWidth: 180,
      headerName: t('pages.templates.usage.content_item_column'),
      resizable: true,
      renderCell: ({ row }) => <ContentItemName contentItem={row} />,
    },
    {
      field: 'templateVersion',
      width: 80,
      headerName: t('common.labels.version'),
      resizable: true,
      valueGetter: (_value, row) => `v${row.templateVersion.version}`,
    },
    {
      field: 'contentFolder',
      flex: 1,
      minWidth: 150,
      headerName: t('pages.templates.usage.folder_column'),
      resizable: true,
      renderCell: ({ row }) => <ContentFolderLink contentItem={row} />,
    },
    {
      field: 'network',
      flex: 1,
      minWidth: 120,
      headerName: t('pages.templates.usage.network_column'),
      resizable: true,
      valueGetter: (_value, row) => row.contentFolder.network.name,
    },
    {
      field: 'createdAt',
      flex: 1,
      minWidth: 160,
      headerName: t('common.labels.created'),
      resizable: true,
      renderCell: ({ row }) => {
        if (!row.createdAt) return null;
        return (
          <Tooltip title={formatTimeAgo(row.createdAt)}>
            <span>{formatDate(row.createdAt)}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'updatedAt',
      flex: 1,
      minWidth: 160,
      headerName: t('pages.templates.usage.updated_column'),
      resizable: true,
      renderCell: ({ row }) => {
        if (!row.updatedAt) return null;
        return (
          <Tooltip title={formatTimeAgo(row.updatedAt)}>
            <span>{formatDate(row.updatedAt)}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      align: 'right',
      width: 60,
      resizable: false,
      getActions: ({ row }) => [<UsageMenu key={`more-${row.id}`} contentItem={row} />],
    },
  ];
};

const NoRowsOverlay = () => {
  const { t } = useTranslation();

  return (
    <EmptyState
      header={t('pages.templates.usage.no_content_items_header')}
      description={t('pages.templates.usage.no_content_items_description')}
    />
  );
};

export const TemplateUsage = () => {
  const { templateId: templateIdParam } = useParams<{ templateId: string }>();
  const templateId = Number(templateIdParam);
  const navigate = useNavigate();
  const location = useLocation();
  const link = useLink();
  const columns = useColumns();
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});
  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumAndDown = useMediaQuery(theme.breakpoints.down('md'));

  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const { data, loading } = useQuery(TemplateUsageDocument, {
    variables: { templateId, page, perPage, search },
    onCompleted: (data) => handleOutOfBounds(data.template?.contentItems.pageInfo),
  });

  const contentItems = data?.template?.contentItems.nodes || [];
  const totalCount = useTotalCount(data?.template?.contentItems.pageInfo.nodesCount);

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      id: !isSmallAndDown,
      templateVersion: !isSmallAndDown,
      contentFolder: true,
      network: !isMediumAndDown,
      createdAt: true,
      updatedAt: !isSmallAndDown,
    }));
  }, [isMediumAndDown, isSmallAndDown]);

  const handleRowClick = useCallback(
    (params: GridRowParams<ContentItem>) => {
      navigate(
        link(
          `/content/${params.row.contentFolder.id}/apps/${params.row.id}/settings`,
          params.row.contentFolder.network.id,
        ),
        {
          state: { returnTo: location.fullPath },
        },
      );
    },
    [link, location.fullPath, navigate],
  );

  return (
    <Layout noPadding>
      <ListPageContainer sx={{ height: '100%' }}>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <SearchBar
                onChange={(value) =>
                  setSearchParams((params) => {
                    params.set('search', value);
                    params.set('page', '1');
                    return params;
                  })
                }
                placeholder={t('pages.templates.usage.search_placeholder')}
                search={search}
              />
              <Count
                selectedCount={0}
                totalCount={totalCount}
                thing="pages.templates.usage.content_item_thing_plural"
              />
            </ListActions>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <ScrollableDataGrid
                  columns={columns}
                  columnVisibilityModel={showColumns}
                  loading={loading}
                  onColumnVisibilityModelChange={setShowColumns}
                  onRowClick={handleRowClick}
                  rows={contentItems}
                  slots={{
                    columnResizeIcon: () => null,
                    noRowsOverlay: NoRowsOverlay,
                  }}
                />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
        </ListContainer>
      </ListPageContainer>
    </Layout>
  );
};
